import { ContactDetailsEnum } from "@/Orchestrator/Types/Enums/globalDefaultEnum";
import { countries } from "./countries";

// Longest-match first so ambiguous prefixes like "+1" vs "+1242" resolve to
// the most specific country code. Precomputed at module load — the country
// list is static.
const SORTED_CODES = countries
    .map((c) => c.countryCallingCode.replace(/\+/g, ""))
    .sort((a, b) => b.length - a.length);

// LRI…PDI (U+2066/U+2069) isolate the number as left-to-right so RTL layouts
// don't shuffle it into "797604620 (213+)" — the digits and "+" keep their
// logical order regardless of the surrounding paragraph direction.
const ltrIsolate = (s: string): string => `⁦${s}⁩`;

// The contact centre number is displayed as a vanity label — "800 RAIL (800
// 7245)" — where the parenthesised digits are the dialable form. Stripping
// non-digits off the whole label yields 8008007245, since "800" appears twice.
const toTelHref = (display: string): string => {
    const dialable = display.match(/\(([^)]*\d[^)]*)\)/)?.[1] ?? display;
    const digits = dialable.replace(/\D/g, "");
    // Only an explicit "+" marks an international number. UAE toll-free 800
    // numbers are dialled nationally and must not gain one.
    return digits ? `tel:${display.startsWith("+") ? "+" : ""}${digits}` : "";
};

// The CMS serves display and dial as one field, so the link is derived from
// whatever string we render — falling back to the built-in number if the CMS
// value carries no digits.
export const contactTelHref = (cmsPhone?: string | null): string =>
    toTelHref(cmsPhone?.trim() ?? "") || toTelHref(ContactDetailsEnum.PHONE);

export const formatPhoneWithCode = (
    phone: string | undefined | null,
): string => {
    if (!phone) return "";
    const raw = phone.replace(/\+/g, "");
    const match = SORTED_CODES.find((code) => raw.startsWith(code));
    if (match) return ltrIsolate(`+${match} ${raw.slice(match.length)}`);
    return ltrIsolate(`+${raw}`);
};
